import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';

/**
 * Tiny copy-to-clipboard button used under project IDs in the board
 * tables. Renders the clipboard icon by default and flips to a green
 * checkmark for ~1.2s after a successful copy so the user has clear
 * confirmation without needing a toast.
 *
 * Falls back to the legacy `document.execCommand('copy')` path on
 * environments where `navigator.clipboard.writeText` is unavailable
 * (older Safari iframe, http context, etc.).
 */
const CopyButton = ({ value, label = 'Copy', size = 12, testid }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!value) return;
    const ok = (txt) => { setCopied(true); setTimeout(() => setCopied(false), 1200); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(value).then(ok).catch(() => {
        // Fallback below
        try {
          const ta = document.createElement('textarea');
          ta.value = value;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
          ok();
        } catch { /* swallow */ }
      });
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={copied ? 'Copied' : `${label}: ${value}`}
      aria-label={copied ? 'Copied' : label}
      className={`inline-flex items-center gap-1 text-[10px] ${copied ? 'text-green-600' : 'text-gray-400 hover:text-[#5B8DB8]'} transition-colors cursor-pointer focus:outline-none`}
      data-testid={testid}
    >
      {copied ? <Check size={size} /> : <Copy size={size} />}
      <span>{copied ? 'Copied' : label}</span>
    </button>
  );
};

export default CopyButton;
